import React, { useMemo, useRef } from 'react'
import { Controller, useForm, useFieldArray } from 'react-hook-form'
import type { AirDatepickerOptions } from 'air-datepicker'
import Card from '../components/Card'
import { usePortal } from '../hooks/usePortal'
import { createPortal } from 'react-dom'
import { ERROR_DICT, errorDict } from '../utils/errorDict'
import TextField from '../components/TextField'
import {
  EMAIL_VALIDATION_PATTERN,
  NAME_VALIDATION_PATTERN,
  PHONE_VALIDATION_PATTERN,
  TELEGRAM_VALIDATION_PATTERN
} from '../constants'
import Btn from '../components/Btn'
import { axiosInstance, useAxios } from '../hooks/useAxios'
import { toast } from 'react-toastify'
import Breadcrumbs from '../components/Breadcrumbs'
import CheckboxSingle from '../components/CheckboxSingle'
import SelectForForm from '../components/SelectForForm'
import ImageUpload from '../components/ImageUpload'
import DocumentTemplateCard from '../components/DocumentTemplateCard'
import { getReference } from '../api/getReference'
import { ReferenceItem } from '../server/references/post'
import { useNavigate } from 'react-router'
import { ReactSVG } from 'react-svg'
import BtnIcon from '../components/BtnIcon'
import PageNotification from '../components/PageNotification'
import { getEventById } from '../api/getEventById'
import { useParams } from 'react-router-dom'
import { updateEvent } from '../api/updateEvent'
import { EventBlockKey, PermissionLevel } from '../server/event/get'
const BREADCRUMBS = [
  {
    label: 'Главная',
    path: '/'
  },
  {
    label: 'Мероприятия',
    path: '/events'
  },
  {
    label: 'Редактирование мероприятия',
    path: '/events/:id/edit'
  }
]
const TIME_RANGE_PATTERN =
  /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]-([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/
function normalizeDateValue(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.filter(Boolean).map(String)
  }
  if (typeof value === 'string' && value.trim()) {
    return [value]
  }
  return []
}
function parseRuDate(date: string): Date | null {
  const [day, month, year] = date.split('.').map(Number)
  if (!day || !month || !year) {
    return null
  }
  const parsed = new Date(Date.UTC(year, month - 1, day))
  if (
    parsed.getUTCFullYear() !== year ||
    parsed.getUTCMonth() !== month - 1 ||
    parsed.getUTCDate() !== day
  ) {
    return null
  }
  return parsed
}
function getDiffInDays(from: Date, to: Date): number {
  const diff = to.getTime() - from.getTime()
  return Math.round(diff / (1000 * 60 * 60 * 24))
}
function EventsEditPage(): React.ReactElement {
  const { id } = useParams()
  const formRef = useRef<HTMLFormElement>(null)
  const loadedEventRef = useRef(false)
  const navigate = useNavigate()
  const { fire } = useAxios()
  const { response: types, fire: typesFire } = useAxios()
  const { response: targets, fire: targetsFire } = useAxios()
  const { response: housings, fire: housingsFire } = useAxios()
  const { response: formats, fire: formatsFire } = useAxios()
  const { response: resources, fire: resourcesFire } = useAxios()
  const { response: organizerTypes, fire: organizerTypesFire } = useAxios()
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    formState: { errors },
    watch
  } = useForm<any>({
    defaultValues: {
      name: '',
      secondName: '',
      lastName: '',
      email: '',
      phone: '',
      telegram: '',
      eventName: '',
      eventInfo: '',
      housing: '',
      eventTarget: '',
      eventFormat: '',
      eventLevel: '',
      auditorium: '',
      file: null,
      createLink: false,
      link: '',
      stream: false,
      eventOrganizerType: [],
      eventParticipantsNumber: null,
      eventParticipantsLimit: null,
      confirmParticipants: false,
      isEventOrder: false,
      fileOrder: null,
      resource: '',
      eventDates: []
    }
  })
  const formDataState = watch()
  const eventDates = watch('eventDates') ?? []
  const firstDateValue = watch('eventDates.0.date')
  const eventDateErrors = Array.isArray(errors.eventDates)
    ? errors.eventDates
    : []
  const firstDateValues = normalizeDateValue(firstDateValue)
  const isFirstRangeMode = firstDateValues.length === 2
  const lastBlock = eventDates[eventDates.length - 1]
  const lastDateValues = normalizeDateValue(lastBlock?.date)
  const canAddBlock =
    !isFirstRangeMode &&
    eventDates.length > 0 &&
    lastDateValues.length === 1
  const { fields, append, remove, replace } = useFieldArray({
    control,
    name: 'eventDates'
  })
  const [permissions, setPermissions] = React.useState<
    Record<string, PermissionLevel>
  >({})
  const [loading, setLoading] = React.useState(false)
  const [moderationStatusMessage, setModerationStatusMessage] =
    React.useState('')
  const [uploadedResourceFiles, setUploadedResourceFiles] = React.useState<
    Record<string, any>
  >({})
  const { portal } = usePortal()
  const firstDatepickerOptions = useMemo<AirDatepickerOptions<HTMLInputElement>>(
    () => ({
      range: true,
      multipleDatesSeparator: ' - ',
      view: 'days',
      minView: 'days',
      dateFormat: 'dd.MM.yyyy'
    }),
    []
  )
  const singleDatepickerOptions =
    useMemo<AirDatepickerOptions<HTMLInputElement>>(
      () => ({
        range: false,
        multipleDatesSeparator: ' - ',
        view: 'days',
        minView: 'days',
        dateFormat: 'dd.MM.yyyy'
      }),
      []
    )
  const removeUploadedResourceFile = (resourceKey: string) => {
    setUploadedResourceFiles((prev) => {
      const next = { ...prev }
      delete next[resourceKey]
      return next
    })
    setValue(`${resourceKey}ResourcesFile`, null)
    setValue(`${resourceKey}Resources`, true)
  }
  const preload = async () => {
    await Promise.all([
      typesFire(getReference('event-types', { all: true })),
      targetsFire(getReference('event-targets', { all: true })),
      housingsFire(getReference('event-housings', { all: true })),
      formatsFire(getReference('event-formats', { all: true })),
      resourcesFire(getReference('event-resources', { all: true })),
      organizerTypesFire(getReference('event-organizer-types', { all: true }))
    ])
  }
  React.useLayoutEffect(() => {
    void preload()
  }, [])
  React.useEffect(() => {
    if (!id || loadedEventRef.current) {
      return
    }
    loadedEventRef.current = true
    void fire(getEventById(id)).then(({ error, response }) => {
      if (error) {
        toast.error(error.message)
        return
      }
      const data = response?.data ?? {}
      Object.keys(data).forEach((key) => {
        if (key === 'eventDates') {
          replace(Array.isArray(data[key]) ? data[key] : [])
          return
        }
        const value = data[key]
        setValue(key, typeof value === 'string' ? value.trim() : value)
      })
      setPermissions(data?.permissions?.blocks ?? {})
      if (data?.resources) {
        setUploadedResourceFiles(data.resources)
        Object.keys(data.resources).forEach((key) => {
          setValue(`${key}Resources`, true)
        })
      }
      if (data?.moderationStatusMessage) {
        setModerationStatusMessage(data.moderationStatusMessage)
      }
    })
  }, [id, fire, replace, setValue])
  React.useEffect(() => {
    if (!isFirstRangeMode || fields.length <= 1) {
      return
    }
    const firstBlock = getValues('eventDates.0')
    replace(firstBlock ? [firstBlock] : [])
  }, [isFirstRangeMode, fields.length, getValues, replace])
  const isBlockDisabled = (block: EventBlockKey): boolean => {
    return permissions[block] !== 'edit'
  }
  const handleAppendEventDate = () => {
    if (canAddBlock) {
      append({})
    }
  }
  const validateEventDate = (value: unknown, index: number) => {
    const currentDates = normalizeDateValue(value)
    if (index === 0) {
      if (currentDates.length === 0) {
        return ERROR_DICT.required
      }
      return true
    }
    if (isFirstRangeMode) {
      return 'Если в первом блоке указан период, все следующие блоки недоступны'
    }
    if (currentDates.length === 0) {
      return ERROR_DICT.required
    }
    if (currentDates.length > 1) {
      return 'В дополнительных блоках можно указать только одну дату'
    }
    const previousDates = normalizeDateValue(eventDates?.[index - 1]?.date)
    if (previousDates.length !== 1) {
      return 'Даты должны идти подряд'
    }
    const previousDate = parseRuDate(previousDates[0])
    const currentDate = parseRuDate(currentDates[0])
    if (!previousDate || !currentDate) {
      return true
    }
    if (getDiffInDays(previousDate, currentDate) !== 1) {
      return 'Даты должны идти подряд'
    }
    return true
  }
  const validateEventTime = (value: unknown, index: number) => {
    const currentDates = normalizeDateValue(eventDates?.[index]?.date)
    if (currentDates.length > 0 && !value) {
      return ERROR_DICT.required
    }
    if (value && !TIME_RANGE_PATTERN.test(String(value))) {
      return ERROR_DICT.time
    }
    return true
  }
  const renderActionButton = (
    index: number
  ): React.ReactElement | undefined => {
    if (isBlockDisabled('eventInfo')) {
      return undefined
    }
    if (index === 0 && canAddBlock) {
      return (
        <BtnIcon
          type="button"
          className={'text-field__add'}
          onClick={handleAppendEventDate}
        >
          <ReactSVG src={'/i/svg/plus.svg'} wrapper={'span'} />
        </BtnIcon>
      )
    }
    if (index > 0) {
      return (
        <BtnIcon
          type="button"
          className={'text-field__add'}
          onClick={() => remove(index)}
        >
          <ReactSVG src={'/i/svg/x.svg'} wrapper={'span'} />
        </BtnIcon>
      )
    }
    return undefined
  }
  const onSubmit = async (data: any): Promise<void> => {
  const formData = new FormData()
  Object.entries(data).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return
    }
    if (key === 'file') {
      if (value instanceof File) {
        formData.append('file', value)
      } else if (value && typeof value === 'object') {
        formData.append('file', JSON.stringify(value))
      }
      return
    }
    if (key === 'fileOrder') {
      if (value instanceof File) {
        formData.append('fileOrder', value)
      } else if (value && typeof value === 'object') {
        formData.append('fileOrder', JSON.stringify(value))
      }
      return
    }
    if (key === 'eventOrganizerType' && Array.isArray(value)) {
      value.forEach((item) => {
        if (item !== undefined && item !== null && item !== '') {
          formData.append('eventOrganaizerType[]', String(item))
        }
      })
      return
    }
    if (key.endsWith('Resources')) {
      const resourceKey = key.replace(/Resources$/, '')
      if (value === true) {
        formData.append(`resourceState[${resourceKey}]`, 'true')
      }
      return
    }
    if (key.endsWith('ResourcesFile')) {
      const resourceKey = key.replace(/ResourcesFile$/, '')
      if (value instanceof File) {
        formData.append(`resources[${resourceKey}]`, value)
      }
      return
    }
    if (typeof value === 'object') {
      formData.append(key, JSON.stringify(value))
      return
    }
    formData.append(key, String(value))
  })
  try {
    setLoading(true)
    formData.forEach((element)=>{
      console.log(element)
    })
    await axiosInstance(updateEvent(id as string, formData))
    toast.success('Мероприятие успешно сохранено')
    navigate('/events')
  } catch (error: any) {
    toast.error(error.message)
  } finally {
    setLoading(false)
  }
}
  return (
    <div className="page">
      <Breadcrumbs className="page__breadcrumbs" breadcrumbs={BREADCRUMBS} />
      {portal &&
        createPortal(<Breadcrumbs breadcrumbs={BREADCRUMBS} />, portal)}
      <header className="page__head">
        <h1>Редактирование мероприятия</h1>
      </header>
      {moderationStatusMessage && (
        <PageNotification
          text={moderationStatusMessage}
          className={'page__note'}
        />
      )}
      <form ref={formRef} onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <header className={'card-head text-h3'}>Личная информация</header>
          <div className={'card-content'}>
            <div className={'row row-gap-20 row-mobile-none'}>
              <TextField
                disabled
                className={'u-flexGrow1'}
                control={control}
                rules={{
                  required: true,
                  pattern: {
                    value: NAME_VALIDATION_PATTERN,
                    message: ''
                  }
                }}
                hasError={!!errors.lastName}
                errorText={errorDict(errors.lastName)}
                name="lastName"
                label="Фамилия"
                required
              />
              <TextField
                disabled
                className={'u-flexGrow1'}
                control={control}
                rules={{
                  required: true,
                  pattern: {
                    value: NAME_VALIDATION_PATTERN,
                    message: ''
                  }
                }}
                hasError={!!errors.name}
                errorText={errorDict(errors.name)}
                name="name"
                label="Имя"
                required
              />
              <TextField
                disabled
                className={'u-flexGrow1'}
                control={control}
                rules={{
                  required: true,
                  pattern: {
                    value: NAME_VALIDATION_PATTERN,
                    message: ''
                  }
                }}
                hasError={!!errors.secondName}
                errorText={errorDict(errors.secondName)}
                name="secondName"
                label="Отчество"
                required
              />
            </div>
            <div className={'row row-gap-20 row-mobile-none'}>
              <TextField
                disabled
                className={'u-flexGrow1 mb-lg-0 mb-md-10'}
                mask={'+7 (999) 999-99-99'}
                control={control}
                rules={{
                  required: true,
                  pattern: { value: PHONE_VALIDATION_PATTERN, message: '' }
                }}
                hasError={!!errors.phone}
                errorText={
                  errors.phone?.type === 'pattern'
                    ? ERROR_DICT.phone
                    : errorDict(errors.phone)
                }
                name="phone"
                type="tel"
                label="Номер телефона"
                required
              />
              <TextField
                disabled
                className={'u-flexGrow1 mb-lg-0 mb-md-10'}
                control={control}
                name="email"
                type="email"
                label="Электронная почта"
                required
                rules={{
                  required: true,
                  pattern: { value: EMAIL_VALIDATION_PATTERN, message: '' }
                }}
                errorText={
                  errors.email?.type === 'pattern'
                    ? ERROR_DICT.email
                    : errorDict(errors.email)
                }
              />
              <TextField
                className={'u-flexGrow1 mb-lg-0 mb-md-0'}
                control={control}
                name="telegram"
                type="text"
                label="Телеграм"
                rules={{
                  required: false,
                  pattern: {
                    value: TELEGRAM_VALIDATION_PATTERN,
                    message: ''
                  }
                }}
                hasError={!!errors.telegram}
                errorText={
                  errors.telegram?.type === 'pattern'
                    ? ERROR_DICT.telegram
                    : errorDict(errors.telegram)
                }
              />
            </div>
          </div>
        </Card>
        <Card className={'mt-md-5 mt-sm-4'}>
          <header className={'card-head text-h3'}>
            Информация о мероприятии
          </header>
          <div className={'card-content'}>
            <TextField
              className={'u-flexGrow1'}
              control={control}
              rules={{
                required: true
              }}
              hasError={!!errors.eventName}
              errorText={
                errors.eventName?.type === 'pattern'
                  ? ERROR_DICT.required
                  : errorDict(errors.eventName)
              }
              name="eventName"
              type="text"
              label="Название мероприятия"
              required
              disabled={isBlockDisabled('eventInfo')}
            />
            <div className={'row row-gap-20 row-mobile-none'}>
              <SelectForForm
                className={'u-flexGrow1'}
                control={control}
                name="eventOrganizerType"
                label="Тип организатора"
                multiple
                options={organizerTypes?.data ?? []}
              />
              <SelectForForm
                className={'u-flexGrow1'}
                control={control}
                name="eventFormat"
                label="Формат мероприятия"
                options={formats?.data ?? []}
                disabled={isBlockDisabled('eventInfo')}
              />
            </div>
            <div className={'row row-gap-20 row-mobile-none'}>
              <SelectForForm
                className={'u-flexGrow1'}
                control={control}
                name="eventLevel"
                label="Уровень мероприятия"
                options={types?.data ?? []}
                disabled={isBlockDisabled('eventInfo')}
              />
              <SelectForForm
                className={'u-flexGrow1'}
                control={control}
                name="eventTarget"
                label="Целевая аудитория"
                options={targets?.data ?? []}
                disabled={isBlockDisabled('eventInfo')}
              />
            </div>
            <div className={'row row-gap-20 row-mobile-none'}>
              <TextField
                className={'u-flexGrow1'}
                control={control}
                rules={{
                  required: false
                }}
                name="eventParticipantsNumber"
                type="number"
                label="Планируемое количество участников"
                disabled={isBlockDisabled('eventInfo')}
              />
              <TextField
                className={'u-flexGrow1'}
                control={control}
                rules={{
                  required: false
                }}
                name="eventParticipantsLimit"
                type="number"
                label="Лимит участников"
                disabled={isBlockDisabled('eventInfo')}
              />
            </div>
            <CheckboxSingle
              name={'confirmParticipants'}
              control={control}
              className={'text-body-bold'}
              disabled={isBlockDisabled('eventInfo')}
            >
              Подтверждать участников вручную
            </CheckboxSingle>
            <div className={'row row-gap-20 row-mobile-none'}>
              <SelectForForm
                className={'u-flexGrow1'}
                control={control}
                name={'housing'}
                label={'Корпус'}
                options={housings?.data ?? []}
                rules={{ required: true }}
                hasError={!!errors.housing}
                errorText={errorDict(errors.housing)}
                required
                disabled={isBlockDisabled('eventInfo')}
              />
              <TextField
                className={'u-flexGrow1'}
                control={control}
                rules={{
                  required: true
                }}
                hasError={!!errors.auditorium}
                errorText={
                  errors.auditorium?.type === 'pattern'
                    ? ERROR_DICT.required
                    : errorDict(errors.auditorium)
                }
                name="auditorium"
                label="Пространство/аудитория"
                required
                disabled={isBlockDisabled('eventInfo')}
              />
            </div>
            {fields.length === 0 ? (
              <div className={'row row-gap-20 row-mobile-none'}>
                <TextField
                  className={'u-flexGrow1'}
                  control={control}
                  rules={{
                    validate: (value) => validateEventDate(value, 0)
                  }}
                  hasError={!!eventDateErrors[0]?.date}
                  errorText={
                    (eventDateErrors[0]?.date?.message as string) ||
                    errorDict(eventDateErrors[0]?.date)
                  }
                  datepickerOptions={firstDatepickerOptions}
                  type="date"
                  name={`eventDates.0.date`}
                  label="Даты проведения"
                  required
                  icon="/i/svg/calendar.svg"
                  disabled={isBlockDisabled('eventInfo')}
                >
                  {canAddBlock && !isBlockDisabled('eventInfo') ? (
                    <BtnIcon
                      type="button"
                      className={'text-field__add'}
                      onClick={handleAppendEventDate}
                    >
                      <ReactSVG src={'/i/svg/plus.svg'} wrapper={'span'} />
                    </BtnIcon>
                  ) : undefined}
                </TextField>
                <TextField
                  className={'u-flexGrow1'}
                  control={control}
                  mask={'99:99-99:99'}
                  hasError={!!eventDateErrors[0]?.time}
                  rules={{
                    validate: (value) => validateEventTime(value, 0)
                  }}
                  errorText={
                    (eventDateErrors[0]?.time?.message as string) ||
                    errorDict(eventDateErrors[0]?.time)
                  }
                  name={`eventDates.0.time`}
                  type="text"
                  label="Время проведения"
                  icon="/i/svg/clock.svg"
                  disabled={isBlockDisabled('eventInfo')}
                />
              </div>
            ) : (
              <>
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className={'row row-gap-20 row-mobile-none'}
                  >
                    <TextField
                      className={'u-flexGrow1'}
                      control={control}
                      disabled={isBlockDisabled('eventInfo')}
                      rules={{
                        validate: (value) => validateEventDate(value, index)
                      }}
                      hasError={!!eventDateErrors[index]?.date}
                      errorText={
                        (eventDateErrors[index]?.date?.message as string) ||
                        errorDict(eventDateErrors[index]?.date)
                      }
                      datepickerOptions={
                        index === 0
                          ? firstDatepickerOptions
                          : singleDatepickerOptions
                      }
                      type="date"
                      name={`eventDates.${index}.date`}
                      label="Даты проведения"
                      required
                      icon="/i/svg/calendar.svg"
                    >
                      {renderActionButton(index)}
                    </TextField>
                    <TextField
                      disabled={isBlockDisabled('eventInfo')}
                      className={'u-flexGrow1'}
                      control={control}
                      mask={'99:99-99:99'}
                      hasError={!!eventDateErrors[index]?.time}
                      rules={{
                        validate: (value) => validateEventTime(value, index)
                      }}
                      errorText={
                        (eventDateErrors[index]?.time?.message as string) ||
                        errorDict(eventDateErrors[index]?.time)
                      }
                      name={`eventDates.${index}.time`}
                      type="text"
                      label="Время проведения"
                      icon="/i/svg/clock.svg"
                    />
                  </div>
                ))}
              </>
            )}
            <div className={'row row-gap-20 row-mobile-none'}>
              <TextField
                disabled={isBlockDisabled('eventInfo')}
                helpText={'Максимум 6000 символов'}
                textarea
                label={'Расскажите о вашем мероприятии*'}
                rules={{
                  required: true,
                  maxLength: 6000
                }}
                hasError={!!errors.eventInfo}
                errorText={errorDict(errors.eventInfo)}
                name={'eventInfo'}
                control={control}
                className={'mb-md-5 mb-sm-4'}
                required
              />
            </div>
          </div>
        </Card>
        <Card className={'mt-md-5 mt-sm-4'}>
          <header className={'card-head text-h3'}>Обложка мероприятия</header>
          <div className={'card-content'}>
            <Controller
              control={control}
              name={'file'}
              rules={{ required: false }}
              render={({ field: { value, onChange } }) => (
                <ImageUpload
                  disabled={isBlockDisabled('cover')}
                  file={value}
                  onChangeFile={onChange}
                  text={
                    'Формат: .jpg, .png и .bmp, размер от 400 пикселей в ширину и высоту, вес до 2 MB.'
                  }
                  note={
                    'Вы сможете добавить или изменить баннер в любой момент.'
                  }
                />
              )}
            />
            <CheckboxSingle
              disabled={isBlockDisabled('cover')}
              name={'isEventOrder'}
              control={control}
              className={'text-body-bold mt-md-8 mt-sm-6 mb-md-0 mb-sm-0'}
            >
              Есть ли приказ на мероприятие?
            </CheckboxSingle>
            {formDataState.isEventOrder && (
              <div className={'mt-md-8 mt-sm-6'}>
                <Controller
                  control={control}
                  name={'fileOrder'}
                  rules={{ required: false }}
                  render={({ field: { value, onChange } }) => (
                    <ImageUpload
                      file={value}
                      disabled={isBlockDisabled('cover')}
                      onChangeFile={onChange}
                      text={
                        'Формат: .pdf, .jpg, .png, .doc,  и .docx, вес до 2 MB.'
                      }
                    />
                  )}
                />
              </div>
            )}
          </div>
        </Card>
        <Card className={'mt-md-5 mt-sm-4'}>
          <header className={'card-head text-h3'}>Выбор ресурсов</header>
          <div className={'card-content'}>
            <div className={'card-wrapper card-wrapper-mobile _align-start'}>
              {resources?.data?.map(
                (resource: ReferenceItem, index: number) => {
                  const template = (resource as any).data
                  const uploaded = uploadedResourceFiles?.[resource.value]
                  return (
                    <div className={'card-checkbox'} key={index}>
                      <CheckboxSingle
                        disabled={isBlockDisabled('resources')}
                        name={`${resource.value}Resources`}
                        control={control}
                        className={'text-body mb-md-0 mb-sm-0'}
                      >
                        {resource.label}
                      </CheckboxSingle>
                      {formDataState[`${resource.value}Resources`] && (
                        <DocumentTemplateCard
                          disabled={isBlockDisabled('resources')}
                          control={control}
                          setValue={setValue}
                          item={{
                            ...(resource as any),
                            template: template as any
                          }}
                          isActive={formDataState[`${resource.value}Resources`]}
                          uploadedFile={uploaded ?? null}
                          onRemoveUploaded={() =>
                            removeUploadedResourceFile(resource.value)
                          }
                        />
                      )}
                    </div>
                  )
                }
              )}
            </div>
            <TextField
              className={'u-flexGrow1 mt-md-8 mb-md-0 mb-sm-0 mt-sm-6'}
              control={control}
              disabled={isBlockDisabled('resources')}
              rules={{
                required: false
              }}
              hasError={!!errors.link}
              errorText={
                errors.link?.type === 'pattern'
                  ? ERROR_DICT.required
                  : errorDict(errors.link)
              }
              name="resource"
              type="text"
              label="Другие"
            />
          </div>
        </Card>
        <Card className={'mt-md-5 mt-sm-4'}>
          <header className={'card-head text-h3'}>ВКС</header>
          <div className={'card-content'}>
            <CheckboxSingle
              disabled={isBlockDisabled('vks')}
              name={'createLink'}
              control={control}
              className={'text-body-bold mt-md-4 mt-sm-3 mb-md-5 mb-sm-5'}
            >
              Необходимо создать ссылку на ВКС
            </CheckboxSingle>
            <CheckboxSingle
              disabled={isBlockDisabled('vks')}
              name={'stream'}
              control={control}
              className={'text-body-bold mt-md-4 mt-sm-3 mb-md-5 mb-sm-6'}
            >
              Необходимо организовать трансляцию
            </CheckboxSingle>
            <TextField
              disabled={isBlockDisabled('vks')}
              className={'u-flexGrow1 mb-md-0 mb-sm-0'}
              control={control}
              rules={{
                required: false
              }}
              hasError={!!errors.link}
              errorText={
                errors.link?.type === 'pattern'
                  ? ERROR_DICT.required
                  : errorDict(errors.link)
              }
              name="link"
              type="text"
              label="Внешняя ссылка для проведения ВКС"
            />
          </div>
        </Card>
        <Btn
          className={'mt-md-8 mt-sm-6'}
          disabled={loading}
          loading={loading}
          type={'submit'}
          onClick={() => {
            setTimeout(() => {
              const errorEl = formRef.current?.querySelector('._has-error')
              errorEl?.scrollIntoView({ behavior: 'smooth' })
            })
          }}
        >
          Сохранить изменения
        </Btn>
      </form>
    </div>
  )
}
export default EventsEditPage